import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import FollowButton from './FollowButton';
import CoverRenderer from '../Storybook/CoverRenderer';

interface PublicProfile {
  penName?: string;
  avatar?: string;
  bio?: string;
  profileVisible?: boolean;
  followerCount?: number;
}

const AuthorProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [stories, setStories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      try {
        const storiesSnap = await getDocs(
          query(collection(db, 'publishedStories'), where('userId', '==', userId))
        );
        setStories(storiesSnap.docs.map(d => ({ ...d.data(), storyId: d.id })));
  
        // The public profile doc is optional: it only adds bio and follower count
        try {
          const snap = await getDoc(doc(db, 'publicProfiles', userId));
          setProfile(snap.exists() ? (snap.data() as PublicProfile) : {});
        } catch {
          setProfile({});
        }
      } catch (err) {
        console.error('Error loading writer:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

useEffect(() => {
  const handleResize = () => setIsMobile(window.innerWidth <= 768);
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

  const [genre, setGenre] = useState('All');

  if (loading) return <div style={{ padding: '40px' }}>Loading...</div>;

  const first: any = stories[0];
const penName = profile?.penName || first?.authorPseudonym;
const avatar = profile?.avatar || first?.authorAvatar || '👤';

if (!penName) {
  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2>We couldn't find that writer.</h2>
      <button onClick={() => navigate(-1)} style={{ minHeight: '44px', padding: '10px 24px', borderRadius: '9999px', border: '2px solid #6a37d4', background: 'white', color: '#6a37d4', fontWeight: 800, cursor: 'pointer' }}>
        Go Back
      </button>
    </div>
  );
}

const C = {
    primary: '#6a37d4', primaryLight: '#ae8dff', primaryContainer: '#ede8ff',
    secondary: '#006947', secondaryContainer: '#d1fae5',
    tertiary: '#6f5900', tertiaryContainer: '#fed01b',
    surface: '#f3f6ff', surfaceLow: '#eaf1ff', surfaceHigh: '#d6e3f9',
    onBackground: '#252f3c', onSurfaceVariant: '#525c6a',
  };
  
  const stripHtml = (html: string): string =>
    html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  
  const genreStyle = (tag?: string): React.CSSProperties => {
    const map: Record<string, { bg: string; color: string }> = {
      'Fantasy':   { bg: C.primaryContainer, color: C.primary },
      'Sci-Fi':    { bg: C.secondaryContainer, color: C.secondary },
      'Mystery':   { bg: '#dbeafe', color: '#1d4ed8' },
      'Adventure': { bg: C.tertiaryContainer, color: C.tertiary },
      'Funny':     { bg: '#fce7f3', color: '#9d174d' },
    };
    const entry = Object.entries(map).find(([k]) => tag?.includes(k));
    return entry
      ? { background: entry[1].bg, color: entry[1].color }
      : { background: C.surfaceHigh, color: C.onSurfaceVariant };
  };
  
  const publishedMs = (s: any): number =>
    s.publishedAt?.toMillis?.() ?? (s.publishedAt?.seconds ? s.publishedAt.seconds * 1000 : 0);

  
  const sorted = [...stories].sort((a: any, b: any) => publishedMs(b) - publishedMs(a));
  const totalHearts = stories.reduce((sum: number, s: any) => sum + (s.reactions?.heart || 0), 0);
  const totalWords = stories.reduce((sum: number, s: any) => sum + (s.wordCount || 0), 0);

  // Only offer genre pills this writer actually has
  const genreOptions = ['Fantasy', 'Funny', 'Adventure', 'Mystery', 'Sci-Fi'].filter(g =>
    stories.some((s: any) => s.tags?.some((t: string) => t.includes(g)))
  );
  const visibleStories = genre === 'All'
    ? sorted
    : sorted.filter((s: any) => s.tags?.some((t: string) => t.includes(genre)));

  return (
    <div style={{ background: C.surface, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", paddingBottom: '80px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '16px 16px 0' : '24px 48px 0' }}>

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          style={{ minHeight: '44px', padding: '8px 18px', borderRadius: '9999px', border: 'none', background: C.primaryContainer, color: C.primary, fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", marginBottom: '16px' }}
        >
          ‹ Stories
        </button>

        {/* Hero */}
        <section style={{ position: 'relative', overflow: 'hidden', background: C.surfaceLow, borderRadius: '28px', padding: isMobile ? '20px' : '36px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ position: 'absolute', right: -80, top: -96, width: 320, height: 320, borderRadius: '50%', background: `${C.primaryLight}33`, filter: 'blur(48px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', left: -80, bottom: -60, width: 260, height: 260, borderRadius: '50%', background: `${C.tertiaryContainer}40`, filter: 'blur(48px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '16px' : '28px', minWidth: 0, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              {/* Avatar */}
              <div style={{ width: isMobile ? 96 : 128, height: isMobile ? 96 : 128, flexShrink: 0, borderRadius: '28px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, padding: '4px', boxShadow: '0 8px 20px rgba(106,55,212,0.2)' }}>
                <div style={{ width: '100%', height: '100%', borderRadius: '24px', background: C.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '3rem' : '4rem' }}>
                  {avatar}
                </div>
              </div>

              {/* Identity */}
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 900, fontSize: isMobile ? '1.8rem' : '2.4rem', color: C.onBackground, margin: 0 }}>
                    @{penName}
                  </h1>
                  <span style={{ background: C.primaryContainer, color: C.primary, padding: '4px 12px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', fontFamily: "'Epilogue', sans-serif" }}>
                    Author
                  </span>
                </div>

                {/* Bio (only if the writer chose to show it) */}
                {profile?.profileVisible && profile?.bio && (
                  <div style={{ marginTop: '14px', maxWidth: '560px', background: 'rgba(255,255,255,0.8)', borderRadius: '18px', padding: '14px 18px', fontSize: '0.95rem', fontStyle: 'italic', color: C.onBackground, lineHeight: 1.55, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                    “{profile.bio}”
                  </div>
                )}
              </div>
            </div>

            {userId && <FollowButton targetUserId={userId} />}
          </div>

          {/* Stats */}
          <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px', marginTop: isMobile ? '24px' : '32px' }}>
            {[
              { icon: '📚', label: 'Stories Published', value: stories.length, tint: C.primaryContainer },
              { icon: '👥', label: 'Followers', value: profile?.followerCount ?? 0, tint: C.secondaryContainer },
              { icon: '❤️', label: 'Hearts Received', value: totalHearts, tint: '#fee2e2' },
              { icon: '✏️', label: 'Words Penned', value: totalWords.toLocaleString(), tint: `${C.tertiaryContainer}66` },
            ].map(stat => (
              <div key={stat.label} style={{ background: 'rgba(255,255,255,0.9)', borderRadius: '20px', padding: '16px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ width: 48, height: 48, borderRadius: '14px', background: stat.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>
                  {stat.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 900, fontSize: isMobile ? '1.3rem' : '1.6rem', color: C.onBackground, lineHeight: 1.1 }}>{stat.value}</div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 600, color: C.onSurfaceVariant }}>{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Stories */}
        <section style={{ marginTop: isMobile ? '32px' : '48px' }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: '14px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 900, fontSize: '1.7rem', color: C.onBackground, margin: 0 }}>Stories</h2>
              <span style={{ background: C.surfaceHigh, color: C.onBackground, padding: '3px 12px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {stories.length} {stories.length === 1 ? 'Work' : 'Works'}
              </span>
            </div>

            {genreOptions.length > 1 && (
              <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', maxWidth: '100%', paddingBottom: '2px' }}>
                {['All', ...genreOptions].map(g => (
                  <button
                    key={g}
                    onClick={() => setGenre(g)}
                    style={{ padding: '8px 16px', borderRadius: '9999px', border: 'none', whiteSpace: 'nowrap', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", background: genre === g ? C.primary : C.surfaceHigh, color: genre === g ? '#fff' : C.onSurfaceVariant }}
                  >
                    {g === 'All' ? 'All Works' : g}
                  </button>
                ))}
              </div>
            )}
          </div>

          {visibleStories.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 24px', background: '#fff', borderRadius: '24px', color: C.onSurfaceVariant }}>
              <div style={{ fontSize: '3rem', marginBottom: '8px' }}>📚</div>
              {stories.length === 0 ? 'No published stories yet.' : 'No stories in this genre.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '20px' }}>
              {visibleStories.map((s: any) => {
                const ms = publishedMs(s);
                return (
                  <article
                    key={s.storyId}
                    onClick={() => navigate(`/read-story/${s.storyId}`, { state: { story: s } })}
                    style={{ background: '#fff', borderRadius: '24px', padding: isMobile ? '16px' : '20px', display: 'flex', gap: '18px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                  >
                    <div style={{ position: 'relative', width: isMobile ? '96px' : '128px', aspectRatio: '5/8', flexShrink: 0, borderRadius: '14px', overflow: 'hidden', boxShadow: '0 6px 16px rgba(0,0,0,0.15)' }}>
                      <CoverRenderer
                        coverData={s.thumbnail && typeof s.thumbnail === 'string' ? JSON.parse(s.thumbnail) : s.thumbnail}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                      />
                    </div>

                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                          {s.tags?.[0] && (
                            <span style={{ ...genreStyle(s.tags[0]), padding: '3px 12px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 700 }}>
                              {s.tags[0].replace(/[^\w\s-]/g, '').trim()}
                            </span>
                          )}
                          {s.wordCount && <span style={{ fontSize: '0.75rem', color: C.onSurfaceVariant }}>🕒 {Math.ceil(s.wordCount / 200)}m read</span>}
                          <span style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: 600 }}>❤️ {s.reactions?.heart || 0}</span>
                        </div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: '1.15rem', color: C.onBackground, margin: '0 0 8px', lineHeight: 1.3 }}>
                          {s.title}
                        </h3>
                        {s.description && (
                          <p style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, margin: 0, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {stripHtml(s.description)}
                          </p>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '14px', marginTop: '10px' }}>
                        <span style={{ fontSize: '0.75rem', color: C.onSurfaceVariant, fontStyle: 'italic' }}>
                          {ms > 0 && `Published ${new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
                        </span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 800, color: C.primary }}>Read Story →</span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );

};

export default AuthorProfile;